<div class="back-to-welcome-container light-control-host">
  @if (showBackToWelcome) {
    <button class="arpio-button small-button back-to-welcome" (click)="navigateBackToWelcome()">
      <i class="fa-solid fa-arrow-left"></i> Back to Welcome
    </button>
  }
  @if (showBackToWelcome) {
    <div class="back-to-welcome-spacer"></div>
  }
  <div class="content-block light-control-host" id="application-creator-content">
    <div class="header-container" stickyHeight>
      <div>
        <h1>Create an Arpio application</h1>
        <p class="header-description">
          Define which cloud resources to protect with Arpio's automated disaster recovery. Resources in an Arpio
          Application will failover together.
        </p>
      </div>
      <div class="flex-center-row form-controls">
        <button type="button" class="arpio-secondary-button" (click)="cancelButtonClick()">CANCEL</button>
        <button
          type="submit"
          form="new-application-form"
          class="arpio-button margin-left"
          [disabled]="
            !applicationFormModel.valid ||
            applicationFormModel.errors?.['existingAppWithDifferentSourceButSameTargetAccount'] ||
            (showAzMismatchWarning && !azWarningAcknowledged)
          "
        >
          CONTINUE
        </button>
      </div>
    </div>
    <form
      [formGroup]="applicationFormModel"
      (ngSubmit)="onSubmit()"
      class="display-block padding-full"
      id="new-application-form"
    >
      <!-- Step 1: Cloud Provider Selection (only shown when both AWS and Azure are enabled) -->
      @if (showCloudProviderSelector) {
        <h3 id="step-cloud-provider-label" class="block-header"><strong>1.</strong> Select cloud provider</h3>
        <div class="flex-center-row">
          <div class="select-wrapper spacer">
            <select id="cloud-provider-select" class="arpio-select full-width" formControlName="applicationType">
              <option value="" disabled>Select a cloud provider</option>
              <option value="aws">AWS</option>
              <option value="azure">Azure</option>
            </select>
          </div>
        </div>
      }

      <!-- Step 2: Microsoft Entra Directory Setup (only shown when Azure is selected) -->
      @if (applicationType == 'azure') {
        <h3 id="step-entra-label" class="block-header">
          <strong>{{ showCloudProviderSelector ? 2 : 1 }}.</strong> Set up Microsoft Entra directory
        </h3>
        <p class="creator-field-description margin-bottom">
          Select an existing Microsoft Entra directory or add a new one. This is required for Azure applications to
          authenticate and access your Azure resources.
        </p>
        <div class="flex-center-row">
          <div class="select-wrapper" [class.spacer]="entraIdentityProviders.length > 0">
            <select
              id="entra-provider-select"
              formControlName="identityProviderId"
              class="arpio-select full-width"
              [class.error-border]="
                applicationFormModel.get('identityProviderId')?.invalid &&
                applicationFormModel.get('identityProviderId')?.touched
              "
            >
              <option value="" disabled>Select a Microsoft Entra Directory</option>
              @for (idp of entraIdentityProviders; track idp) {
                <option [value]="idp.identityProviderId">{{ idp.name }} ({{ idp.config.tenantId }})</option>
              }
            </select>
          </div>
          <button
            type="button"
            class="arpio-button margin-left"
            (click)="openAddEntraIdpDialog()"
            id="add-entra-button"
          >
            Add Microsoft Entra Directory
          </button>
        </div>
        @if (
          applicationFormModel.get('identityProviderId')?.invalid &&
          applicationFormModel.get('identityProviderId')?.touched
        ) {
          <div class="error margin-top">A Microsoft Entra directory is required for Azure applications.</div>
        }
      }

      <!-- Application Name Step -->
      <h3 id="step-1-label" class="block-header">
        <strong>{{ baseStepNumber }}.</strong>
        Application name
      </h3>
      <div class="flex-center-row">
        <input
          id="application-name-input"
          class="arpio-input spacer"
          type="text"
          formControlName="applicationName"
          placeholder="Application name"
          autocomplete="off"
          focus-element
        />
      </div>

      <div id="navattic-tags-container">
        <h3 id="step-2-label" class="block-header">
          <strong>{{ baseStepNumber + 1 }}.</strong>
          Where do you currently operate this application?
        </h3>
        <div class="flex-center-row">
          <div class="flex-1 full-width">
            <input
              id="source-account-id"
              class="arpio-input full-width"
              type="text"
              formControlName="sourceAccountId"
              [placeholder]="sourceAccountPlaceholder"
            />
          </div>
          <div class="flex-1 margin-left full-width">
            <app-region-dropdown
              formControlName="sourceRegion"
              placeholder="Region"
              extraClasses="application-creator-custom-dropdown"
              [applicationType]="applicationType"
              (regionSelected)="onSourceRegionChanged($event)"
            >
            </app-region-dropdown>
          </div>
        </div>

        <h3 id="step-3-label" class="block-header">
          <strong>{{ baseStepNumber + 2 }}.</strong>
          Where do you want to recover this application?
        </h3>
        <div class="flex-center-row">
          <div class="flex-1 full-width">
            <input
              id="target-account-id"
              class="arpio-input full-width"
              type="text"
              formControlName="targetAccountId"
              [placeholder]="targetAccountPlaceholder"
            />
          </div>
          <div class="flex-1 margin-left full-width">
            <app-region-dropdown
              formControlName="targetRegion"
              placeholder="Region"
              [excludeRegion]="sourceRegion"
              [allowSameRegion]="sameRegionSyncPairsEntitled"
              [compareAccount]="sourceAccountId"
              [currentAccount]="targetAccountId"
              [applicationType]="applicationType"
              extraClasses="application-creator-custom-dropdown"
            >
            </app-region-dropdown>
          </div>
        </div>

        @if (showAzMismatchWarning) {
          <div class="az-warning-notice margin-top">
            <i class="fa-solid fa-circle-exclamation"></i>
            <div>
              <p>
                Your recovery region does not support the availability zone capabilities of your primary region. This
                difference could cause recovery point attempts to fail and impact recovery tests.
              </p>
              <div class="az-warning-acknowledge">
                <input
                  id="az-warning-checkbox"
                  class="arpio-checkbox"
                  type="checkbox"
                  [(ngModel)]="azWarningAcknowledged"
                  [ngModelOptions]="{ standalone: true }"
                />
                <label for="az-warning-checkbox">I acknowledge this warning</label>
              </div>
            </div>
          </div>
        }
      </div>

      <div class="flex-center-row margin-top">
        <h3 id="step-4-label" class="block-header spacer">
          <strong>{{ baseStepNumber + 3 }}.</strong>
          Define your recovery point objective (RPO)
        </h3>
        <h4 class="block-header whitespace-nowrap">{{ rpoText }}</h4>
      </div>
      <rpo-range-slider [enableRealTimeRpos]="enableRealTimeRpo" formControlName="applicationRpo"></rpo-range-slider>
      @if (isRealTimeRpo) {
        <about-real-time-replication [applicationType]="applicationType"></about-real-time-replication>
      }

      @if (
        applicationFormModel.errors?.['existingAppWithSameSourceButDifferentTargetAccount'] &&
        (applicationFormModel.touched || applicationFormModel.dirty)
      ) {
        <div class="error margin-top">
          This application cannot be created because another application ({{
            applicationFormModel.errors['existingAppWithSameSourceButDifferentTargetAccount']
          }}) shares its primary account/region ({{ sourceAccountId }}/{{ sourceRegion }}) and recovery region ({{
            targetRegion
          }}), but uses a different recovery AWS account ID. Applications in a region pair must have distinct primary
          and recovery AWS account IDs.
        </div>
      }

      <!-- Show error if recovery environment is already associated with a different primary -->
      @if (
        applicationFormModel.errors?.['existingAppWithDifferentSourceButSameTargetAccount'] &&
        (applicationFormModel.touched || applicationFormModel.dirty)
      ) {
        <div class="error margin-top">
          <p>
            This recovery environment is already linked to another primary environment ({{
              applicationFormModel.errors['existingAppWithDifferentSourceButSameTargetAccount']
            }}).
          </p>
          <p>
            Arpio supports only a 1:1 mapping between primary and recovery environments. To continue, choose a different
            recovery account and region combination.
          </p>
        </div>
      }

      @if (
        applicationFormModel.errors?.['existingAppWithSameName'] &&
        (applicationFormModel.touched || applicationFormModel.dirty)
      ) {
        <div class="error margin-top">
          This application cannot be created because another application exists with the same name.
        </div>
      }
    </form>

    <!-- IDP Modal for creating new Entra identity providers -->
    <app-idp-modal
      #idpModal
      [accountId]="accountId"
      (save)="onIdpModalSave($event)"
      (cancel)="onIdpModalCancel()"
    ></app-idp-modal>
  </div>
</div>
